import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, Plus, Search, Phone, Mail, Building2, MapPin, 
  Star, Briefcase, CreditCard, Edit, Trash2, CheckCircle, 
  XCircle, RefreshCw, Wrench, Zap, Settings, DollarSign,
  TrendingUp, Clock, AlertCircle, Filter, MoreVertical, User, Eye,
  Package, Calendar, AlertTriangle, Coffee
} from 'lucide-react';
import { toast } from 'sonner';
import { turkeyProvinces, turkeyDistricts } from '../data/turkeyLocations';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Teknisyen Tipleri
const TECHNICIAN_TYPES = {
  hims_team: { label: 'HIMS Ekibi', color: 'blue', icon: Building2 },
  subcontractor: { label: 'Taşeron', color: 'orange', icon: Wrench },
  service_company: { label: 'Servis Firmasi', color: 'purple', icon: Briefcase }
};

// İl kodunu il adından bul
const getProvinceCode = (cityName) => {
  const province = turkeyProvinces.find(p => p.name === cityName);
  return province ? province.code : null;
};

// İl adına göre ilçeleri getir
const getDistrictsByCity = (cityName) => {
  const code = getProvinceCode(cityName);
  return code ? turkeyDistricts[code] || [] : [];
};

// Uzmanlık Alanları
const SPECIALTIES = {
  ac_charger: { label: 'AC Şarj', icon: Zap },
  dc_charger: { label: 'DC Hızlı Şarj', icon: Zap },
  software: { label: 'Yazilim/OCPP', icon: Settings },
  electrical: { label: 'Elektrik', icon: Zap },
  general: { label: 'Genel Bakim', icon: Wrench },
  installation: { label: 'Montaj/Kurulum', icon: Package }
};

// Ödeme Tipleri
const PAYMENT_TYPES = {
  fixed_salary: { label: 'Sabit Maas', icon: DollarSign },
  per_job: { label: 'İş Başı', icon: Briefcase },
  commission: { label: 'Komisyonlu', icon: TrendingUp }
};

// Native Modal Component
const NativeModal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

// Teknisyen Kartı
const TechnicianCard = ({ technician, onEdit, onDelete }) => {
  const typeInfo = TECHNICIAN_TYPES[technician.technician_type] || TECHNICIAN_TYPES.subcontractor;
  const TypeIcon = typeInfo.icon;

  // Üyelik durumu hesaplama
  const getMembershipStatus = () => {
    if (!technician.membership_end) return null;
    
    const today = new Date();
    const endDate = new Date(technician.membership_end);
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return { color: 'red', label: 'Süresi Doldu', iconType: 'expired', days: Math.abs(daysRemaining) };
    } else if (daysRemaining <= 30) {
      return { color: 'amber', label: `${daysRemaining} gün`, iconType: 'warning', days: daysRemaining };
    } else {
      return { color: 'green', label: 'Aktif', iconType: 'active', days: daysRemaining };
    }
  };

  const membershipStatus = getMembershipStatus();

  // Üyelik ikonu render
  const renderMembershipIcon = () => {
    if (!membershipStatus) return null;
    switch(membershipStatus.iconType) {
      case 'expired': return <AlertTriangle className="w-3 h-3" />;
      case 'warning': return <Clock className="w-3 h-3" />;
      case 'active': return <CheckCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full bg-${typeInfo.color}-100 dark:bg-${typeInfo.color}-900/30 flex items-center justify-center`}>
            <TypeIcon className={`w-6 h-6 text-${typeInfo.color}-600 dark:text-${typeInfo.color}-400`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{technician.full_name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full bg-${typeInfo.color}-100 text-${typeInfo.color}-700 dark:bg-${typeInfo.color}-900/30 dark:text-${typeInfo.color}-400`}>
              {typeInfo.label}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {/* Üyelik Durumu Rozeti */}
          {membershipStatus && (
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full
              ${membershipStatus.color === 'red' ? 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400' : ''}
              ${membershipStatus.color === 'amber' ? 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400' : ''}
              ${membershipStatus.color === 'green' ? 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400' : ''}
            `}>
              {renderMembershipIcon()}
              {membershipStatus.label}
            </span>
          )}
          {/* Müsaitlik Durumu - availability_status'a göre */}
          {(() => {
            const status = technician.availability_status || (technician.is_available ? 'available' : 'busy');
            if (status === 'available') {
              return (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" /> Müsait
                </span>
              );
            } else if (status === 'off_duty') {
              return (
                <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 dark:bg-gray-900/30 px-2 py-1 rounded-full">
                  <Coffee className="w-3 h-3" /> İzinli
                </span>
              );
            } else {
              return (
                <span className="flex items-center gap-1 text-xs text-red-600 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full">
                  <XCircle className="w-3 h-3" /> Meşgul
                </span>
              );
            }
          })()}
        </div>
      </div>

      {/* İletişim */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <Phone className="w-4 h-4" />
          <a href={`tel:${technician.phone}`} className="hover:text-primary">{technician.phone}</a>
        </div>
        {technician.email && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Mail className="w-4 h-4" />
            <span>{technician.email}</span>
          </div>
        )}
        {technician.company_name && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Building2 className="w-4 h-4" />
            <span>{technician.company_name}</span>
          </div>
        )}
      </div>

      {/* Uzmanlık Alanları */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(technician.specialties || []).map(spec => {
          const specInfo = SPECIALTIES[spec];
          return specInfo ? (
            <span key={spec} className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded-full">
              {specInfo.label}
            </span>
          ) : null;
        })}
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-3 gap-2 py-3 border-t border-b dark:border-slate-700 mb-3">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-900 dark:text-white">{technician.completed_jobs || 0}</p>
          <p className="text-xs text-slate-500">Tamamlanan</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-yellow-500 flex items-center justify-center gap-1">
            <Star className="w-4 h-4 fill-current" />
            {(technician.average_rating || 0).toFixed(1)}
          </p>
          <p className="text-xs text-slate-500">Puan</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-green-600">{technician.per_job_rate || 0} TL</p>
          <p className="text-xs text-slate-500">İş Başı</p>
        </div>
      </div>

      {/* Aksiyonlar */}
      <div className="flex gap-2">
        <button 
          onClick={() => onEdit(technician)}
          className="flex-1 py-2 px-3 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          <Edit className="w-4 h-4" /> Düzenle
        </button>
        <button 
          onClick={() => onDelete(technician)}
          className="py-2 px-3 text-sm font-medium text-red-600 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Ana Sayfa
export default function TechnicianManagementPage() {
  const { user, getEffectiveRole, getEffectiveSiteId, isHesapYoneticisi, isSuperAdmin, isAdmin } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [pendingTechnicians, setPendingTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAvailable, setFilterAvailable] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'contracts', 'membership'
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [sites, setSites] = useState([]);
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  const isEffectiveSuperAdmin = effectiveRole === 'super_admin';
  
  // Sözleşme modal
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractForm, setContractForm] = useState({
    technician_id: '',
    site_id: '',
    start_date: '',
    end_date: '',
    monthly_fee: 0,
    notes: ''
  });
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [selectedForVerify, setSelectedForVerify] = useState(null);

  // Form state
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    technician_type: 'subcontractor',
    company_name: '',
    specialties: ['general'],
    service_regions: [],
    // Adres alanları
    address: '',
    city: '',
    district: '',
    // Ödeme
    payment_type: 'per_job',
    per_job_rate: 500,
    warranty_rate: 300,
    commission_rate: 0,
    travel_allowance: 150,
    iban: '',
    tax_id: '',
    // Profil
    profile_description: '',
    // Durum
    is_active: true,
    is_available: true,
    create_user_account: false,
    password: ''
  });

  const fetchTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');
      
      let url = `${API_URL}/api/technicians?`;
      if (filterType) url += `technician_type=${filterType}&`;
      if (filterAvailable !== '') url += `is_available=${filterAvailable}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        let data = await res.json();
        
        // Hesap Yöneticisi simülasyonunda sadece sözleşmeli ustaları göster
        if (isEffectiveHesapYoneticisi && effectiveSiteId && data.length > 0) {
          // Önce bu site için sözleşmeleri çek
          try {
            const contractsRes = await fetch(`${API_URL}/api/technician-contracts?site_id=${effectiveSiteId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (contractsRes.ok) {
              const contractsData = await contractsRes.json();
              const contractedTechnicianIds = (contractsData || []).map(c => c.technician_id);
              // Sadece sözleşmeli ustaları filtrele
              data = data.filter(tech => contractedTechnicianIds.includes(tech.id));
            }
          } catch (contractErr) {
            console.warn('Sözleşmeler yüklenemedi, tüm ustalar gösteriliyor:', contractErr);
          }
        }
        
        setTechnicians(data);
      }
    } catch (error) {
      console.error('Teknisyenler yüklenemedi:', error);
      toast.error('Teknisyenler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterAvailable, searchTerm, isEffectiveHesapYoneticisi, effectiveSiteId]);

  // Onay bekleyen ustaları getir
  const fetchPendingTechnicians = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/technicians/pending-verification`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPendingTechnicians(data);
      }
    } catch (error) {
      console.error('Onay bekleyen ustalar yüklenemedi:', error);
    }
  }, []);

  // Siteleri yükle
  const fetchSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        let data = await res.json();
        // Hesap Yöneticisi simülasyonunda sadece kendi sitesi
        if (isEffectiveHesapYoneticisi && effectiveSiteId) {
          data = (data || []).filter(s => s.id === effectiveSiteId);
        }
        setSites(data);
      }
    } catch (error) {
      console.error('Siteler yüklenemedi:', error);
    }
  }, [isEffectiveHesapYoneticisi, effectiveSiteId]);

  // Sözleşmeleri yükle
  const fetchContracts = useCallback(async () => {
    setContractsLoading(true);
    try {
      const token = localStorage.getItem('hims_token');
      let url = `${API_URL}/api/technician-contracts`;
      // Hesap Yöneticisi simülasyonunda site filtresi
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        url += `?site_id=${effectiveSiteId}`;
      }
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        let data = await res.json();
        // Ekstra frontend filtresi
        if (isEffectiveHesapYoneticisi && effectiveSiteId) {
          data = (data || []).filter(c => c.site_id === effectiveSiteId);
        }
        setContracts(data);
      }
    } catch (error) {
      console.error('Sözleşmeler yüklenemedi:', error);
    } finally {
      setContractsLoading(false);
    }
  }, [isEffectiveHesapYoneticisi, effectiveSiteId]);

  // Sözleşme oluştur
  const handleCreateContract = async () => {
    if (!contractForm.technician_id || !contractForm.site_id || !contractForm.start_date || !contractForm.end_date) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/technician-contracts`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contractForm)
      });
      
      if (res.ok) {
        toast.success('Sözleşme oluşturuldu');
        setContractModalOpen(false);
        setContractForm({
          technician_id: '',
          site_id: '',
          start_date: '',
          end_date: '',
          monthly_fee: 0,
          notes: ''
        });
        fetchContracts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Sözleşme oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Usta onayla
  const handleVerifyTechnician = async (techId) => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/technicians/${techId}/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Usta onaylandı');
        fetchPendingTechnicians();
        fetchTechnicians();
        setVerifyModalOpen(false);
        setSelectedForVerify(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Bir hata oluştu');
    }
  };

  // Usta reddet
  const handleRejectTechnician = async (techId, reason) => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/technicians/${techId}/reject?reason=${encodeURIComponent(reason || '')}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Usta reddedildi');
        fetchPendingTechnicians();
        fetchTechnicians();
        setVerifyModalOpen(false);
        setSelectedForVerify(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Reddetme başarısız');
      }
    } catch (error) {
      console.error('Reddetme hatası:', error);
      toast.error('Bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchTechnicians();
    fetchPendingTechnicians();
    fetchSites();
    fetchContracts();
  }, [fetchTechnicians, fetchPendingTechnicians, fetchSites, fetchContracts]);

  const openCreateModal = () => {
    setEditingTechnician(null);
    setForm({
      full_name: '',
      phone: '',
      email: '',
      technician_type: 'subcontractor',
      company_name: '',
      specialties: ['general'],
      service_regions: [],
      address: '',
      city: '',
      district: '',
      payment_type: 'per_job',
      per_job_rate: 500,
      warranty_rate: 300,
      commission_rate: 0,
      travel_allowance: 150,
      iban: '',
      tax_id: '',
      profile_description: '',
      is_active: true,
      is_available: true,
      create_user_account: false,
      password: ''
    });
    setModalOpen(true);
  };

  const openEditModal = (tech) => {
    setEditingTechnician(tech);
    setForm({
      full_name: tech.full_name || '',
      phone: tech.phone || '',
      email: tech.email || '',
      technician_type: tech.technician_type || 'subcontractor',
      company_name: tech.company_name || '',
      specialties: tech.specialties || ['general'],
      service_regions: tech.service_regions || [],
      address: tech.address || '',
      city: tech.city || '',
      district: tech.district || '',
      payment_type: tech.payment_type || 'per_job',
      per_job_rate: tech.per_job_rate || 500,
      warranty_rate: tech.warranty_rate || 300,
      commission_rate: tech.commission_rate || 0,
      travel_allowance: tech.travel_allowance || 150,
      iban: tech.iban || '',
      tax_id: tech.tax_id || '',
      profile_description: tech.profile_description || '',
      is_active: tech.is_active ?? true,
      is_available: tech.is_available ?? true,
      create_user_account: false,
      password: '',
      has_user_account: !!tech.user_id,
      membership_start: tech.membership_start?.slice(0, 10) || '',
      membership_end: tech.membership_end?.slice(0, 10) || ''
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!form.full_name || !form.phone) {
      toast.error('Ad Soyad ve Telefon zorunludur');
      return;
    }

    // Kullanıcı hesabı oluşturulacaksa email ve şifre zorunlu
    if (form.create_user_account && !form.email) {
      toast.error('Kullanıcı hesabı için Email zorunludur');
      return;
    }

    if (form.create_user_account && form.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
      
      if (!token) {
        toast.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        setSaving(false);
        return;
      }
      
      // 1. Kullanıcı hesabı oluştur (eğer seçildiyse)
      let userId = null;
      if (form.create_user_account && form.email && form.password) {
        const userRes = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: form.email,
            full_name: form.full_name,
            password: form.password,
            role: 'calisan', // Usta rolü olarak calisan
            phone: form.phone
          })
        });

        if (userRes.ok) {
          const userData = await userRes.json();
          userId = userData.id;
          toast.success('Kullanıcı hesabı oluşturuldu');
        } else {
          const userErr = await userRes.json();
          let errorMsg = 'Kullanıcı hesabı oluşturulamadı';
          
          // Detaylı hata mesajı
          if (userErr.detail) {
            if (typeof userErr.detail === 'string') {
              if (userErr.detail.includes('email') || userErr.detail.includes('Email')) {
                errorMsg = 'Bu email adresi zaten kullanılıyor';
              } else {
                errorMsg = userErr.detail;
              }
            } else if (Array.isArray(userErr.detail)) {
              // Pydantic validation error
              const firstError = userErr.detail[0];
              if (firstError?.msg) {
                errorMsg = firstError.msg;
              }
            }
          }
          
          toast.error(errorMsg);
          setSaving(false);
          return;
        }
      }

      // 2. Teknisyen bilgilerini kaydet
      const techData = { ...form };
      delete techData.create_user_account;
      delete techData.password;
      delete techData.has_user_account;
      
      // service_regions'ı array olarak garantile
      if (typeof techData.service_regions === 'string') {
        techData.service_regions = techData.service_regions.split(',').map(s => s.trim()).filter(Boolean);
      } else if (!Array.isArray(techData.service_regions)) {
        techData.service_regions = [];
      }
      
      // Yeni kullanıcı oluşturulduysa user_id'yi ekle
      if (userId) {
        techData.user_id = userId;
      }

      const url = editingTechnician 
        ? `/api/technicians/${editingTechnician.id}`
        : `/api/technicians`;
      
      console.log('Teknisyen kaydetme URL:', url, 'Method:', editingTechnician ? 'PUT' : 'POST');
      
      const res = await fetch(url, {
        method: editingTechnician ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(techData),
        redirect: 'follow'
      });

      if (res.ok) {
        toast.success(editingTechnician ? 'Teknisyen güncellendi' : 'Teknisyen oluşturuldu');
        setModalOpen(false);
        fetchTechnicians();
      } else {
        const err = await res.json();
        let errorMsg = err.detail || 'İşlem başarısız';
        // Email hatası kontrolü
        if (errorMsg.toLowerCase().includes('email') && (errorMsg.includes('kayıtlı') || errorMsg.includes('exist') || errorMsg.includes('already'))) {
          errorMsg = 'Bu email adresi zaten kullanılıyor';
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Teknisyen kaydetme hatası:', error);
      toast.error('Sunucuya bağlanılamadı. Lütfen tekrar deneyin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tech) => {
    if (!window.confirm(`${tech.full_name} isimli teknisyeni silmek istediğinize emin misiniz?`)) return;

    try {
      const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
      const res = await fetch(`/api/technicians/${tech.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Teknisyen silindi');
        fetchTechnicians();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Teknisyen silinemedi');
      }
    } catch (error) {
      console.error('Teknisyen silme hatası:', error);
      toast.error('Sunucuya bağlanılamadı');
    }
  };

  const toggleSpecialty = (spec) => {
    setForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(spec)
        ? prev.specialties.filter(s => s !== spec)
        : [...prev.specialties, spec]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Teknisyen Yönetimi
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Usta ve teknisyen ekibinizi yönetin
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          data-testid="add-technician-btn"
        >
          <Plus className="w-5 h-5" />
          Yeni Teknisyen
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Ad, telefon veya firma ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
          >
            <option value="">Tüm Tipler</option>
            {Object.entries(TECHNICIAN_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>

          {/* Availability Filter */}
          <select
            value={filterAvailable}
            onChange={(e) => setFilterAvailable(e.target.value)}
            className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
          >
            <option value="">Tüm Durumlar</option>
            <option value="true">Müsait</option>
            <option value="false">Mesgul</option>
          </select>

          {/* Refresh */}
          <button
            onClick={fetchTechnicians}
            className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{technicians.length}</p>
              <p className="text-sm text-slate-500">Toplam</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{technicians.filter(t => t.is_available).length}</p>
              <p className="text-sm text-slate-500">Müsait</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Wrench className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{technicians.filter(t => t.technician_type === 'subcontractor').length}</p>
              <p className="text-sm text-slate-500">Taşeron</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{technicians.filter(t => t.technician_type === 'hims_team').length}</p>
              <p className="text-sm text-slate-500">HIMS Ekibi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs: All / Pending / Contracts / Membership */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'all'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Users className="w-4 h-4 inline-block mr-2" />
          Tüm Ustalar ({technicians.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'pending'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          Onay Bekleyenler
          {pendingTechnicians.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
              {pendingTechnicians.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('contracts')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'contracts'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Briefcase className="w-4 h-4 inline-block mr-2" />
          Sözleşmeler
        </button>
        <button
          onClick={() => setActiveTab('membership')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'membership'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <CreditCard className="w-4 h-4 inline-block mr-2" />
          Üyelik/Lisans
        </button>
      </div>

      {/* Pending Verification Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingTechnicians.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">Tüm Ustalar Onaylandı</h3>
              <p className="text-slate-500">Şu anda onay bekleyen usta bulunmuyor.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingTechnicians.map(tech => (
                <div key={tech.id} className="bg-white dark:bg-slate-800 rounded-xl border-2 border-orange-200 dark:border-orange-900/50 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <User className="w-7 h-7 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{tech.full_name}</h3>
                          <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-full">
                            Onay Bekliyor
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {tech.phone}
                          </span>
                          {tech.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" /> {tech.email}
                            </span>
                          )}
                          {tech.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" /> {tech.city}{tech.district && `, ${tech.district}`}
                            </span>
                          )}
                        </div>
                        {/* Profil Durumu */}
                        <div className="mt-2 flex items-center gap-2">
                          {tech.profile_completed ? (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Profil Tamamlandı
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 rounded-full flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Profil Eksik
                            </span>
                          )}
                          {tech.specialties?.length > 0 && (
                            <div className="flex gap-1">
                              {tech.specialties.slice(0, 3).map(spec => (
                                <span key={spec} className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">
                                  {SPECIALTIES[spec]?.label || spec}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {tech.profile_description && (
                          <p className="mt-2 text-sm text-slate-500 line-clamp-2">"{tech.profile_description}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => {
                          setSelectedForVerify(tech);
                          setVerifyModalOpen(true);
                        }}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" /> İncele
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Technicians Tab */}
      {activeTab === 'all' && (
        <>
          {/* Technician Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : technicians.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <Users className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Teknisyen Bulunamadı</h3>
              <p className="text-slate-500 mb-4">Henüz teknisyen eklenmemiş veya filtreye uygun sonuç yok.</p>
              <button onClick={openCreateModal} className="px-4 py-2 bg-primary text-white rounded-lg">
                Ilk Teknisyeni Ekle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicians.map(tech => (
                <TechnicianCard
                  key={tech.id}
                  technician={tech}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <div className="space-y-6">
          {/* Yeni Sözleşme Butonu */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Usta-Site Sözleşmeleri</h3>
            <button
              onClick={() => setContractModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Yeni Sözleşme
            </button>
          </div>
          
          {/* Sözleşme Listesi */}
          {contractsLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <Briefcase className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Henüz sözleşme yok
              </h3>
              <p className="text-slate-500">Yeni sözleşme oluşturmak için yukarıdaki butonu kullanın.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contracts.map(contract => {
                const endDate = contract.end_date ? new Date(contract.end_date) : null;
                const today = new Date();
                const daysLeft = endDate ? Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)) : null;
                const isExpiring = daysLeft !== null && daysLeft <= 20 && daysLeft > 0;
                const isExpired = daysLeft !== null && daysLeft <= 0;
                
                return (
                  <div 
                    key={contract.id} 
                    className={`bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border ${
                      isExpired ? 'border-red-500' : isExpiring ? 'border-amber-500' : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    {(isExpiring || isExpired) && (
                      <div className={`mb-3 px-2 py-1 rounded text-xs font-medium ${
                        isExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {isExpired ? 'Süresi Doldu' : `${daysLeft} gün kaldı!`}
                      </div>
                    )}
                    <h4 className="font-semibold text-lg">{contract.technician_name || 'Usta'}</h4>
                    <p className="text-sm text-slate-500 mb-3">{contract.site_name || 'Site'}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Başlangıç:</span>
                        <span>{contract.start_date ? new Date(contract.start_date).toLocaleDateString('tr-TR') : '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Bitiş:</span>
                        <span>{contract.end_date ? new Date(contract.end_date).toLocaleDateString('tr-TR') : '-'}</span>
                      </div>
                      {contract.monthly_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Aylık Ücret:</span>
                          <span className="font-medium">{contract.monthly_fee} ₺</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Membership/License Tab */}
      {activeTab === 'membership' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {technicians.map(tech => {
              const membershipEnd = tech.membership_end ? new Date(tech.membership_end) : null;
              const today = new Date();
              const daysLeft = membershipEnd ? Math.ceil((membershipEnd - today) / (1000 * 60 * 60 * 24)) : null;
              const status = daysLeft === null ? 'none' : daysLeft < 0 ? 'expired' : daysLeft <= 30 ? 'warning' : 'active';
              
              return (
                <div key={tech.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">{tech.full_name}</h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      status === 'expired' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      status === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {status === 'expired' ? 'Süresi Doldu' :
                       status === 'warning' ? `${daysLeft} gün kaldı` :
                       status === 'active' ? 'Aktif' : 'Tanımsız'}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Başlangıç:</span>
                      <span>{tech.membership_start ? new Date(tech.membership_start).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bitiş:</span>
                      <span>{tech.membership_end ? new Date(tech.membership_end).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ödeme Durumu:</span>
                      <span className={tech.membership_fee_active ? 'text-green-600' : 'text-red-600'}>
                        {tech.membership_fee_active ? 'Ödendi' : 'Ödenmedi'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => openEditModal(tech)}
                    className="mt-3 w-full py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                  >
                    Düzenle
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Verification Modal */}
      <NativeModal
        isOpen={verifyModalOpen}
        onClose={() => {
          setVerifyModalOpen(false);
          setSelectedForVerify(null);
        }}
        title="Usta Profili İnceleme"
        size="lg"
      >
        {selectedForVerify && (
          <div className="space-y-6">
            {/* Usta Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <User className="w-5 h-5" /> Kişisel Bilgiler
                </h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <p><span className="text-slate-500">Ad Soyad:</span> <strong>{selectedForVerify.full_name}</strong></p>
                  <p><span className="text-slate-500">Telefon:</span> {selectedForVerify.phone}</p>
                  <p><span className="text-slate-500">E-posta:</span> {selectedForVerify.email || '-'}</p>
                  <p><span className="text-slate-500">TC/Vergi No:</span> {selectedForVerify.tax_id || '-'}</p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" /> Konum Bilgileri
                </h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <p><span className="text-slate-500">İl:</span> {selectedForVerify.city || '-'}</p>
                  <p><span className="text-slate-500">İlçe:</span> {selectedForVerify.district || '-'}</p>
                  <p><span className="text-slate-500">Adres:</span> {selectedForVerify.address || '-'}</p>
                  <p><span className="text-slate-500">Hizmet Bölgeleri:</span> {selectedForVerify.service_regions?.join(', ') || '-'}</p>
                </div>
              </div>
            </div>

            {/* Uzmanlık ve Ödeme */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <Wrench className="w-5 h-5" /> Uzmanlık Alanları
                </h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2">
                    {(selectedForVerify.specialties || []).map(spec => (
                      <span key={spec} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {SPECIALTIES[spec]?.label || spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Ödeme Bilgileri
                </h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <p><span className="text-slate-500">IBAN:</span> {selectedForVerify.iban || '-'}</p>
                  <p><span className="text-slate-500">İş Başı Ücreti:</span> {selectedForVerify.per_job_rate || 0} ₺</p>
                  <p><span className="text-slate-500">Yol Masrafı:</span> {selectedForVerify.travel_allowance || 0} ₺</p>
                </div>
              </div>
            </div>

            {/* Profil Açıklaması */}
            {selectedForVerify.profile_description && (
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Kendini Tanıtımı</h4>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <p className="text-slate-600 dark:text-slate-300">{selectedForVerify.profile_description}</p>
                </div>
              </div>
            )}

            {/* Aksiyon Butonları */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => handleVerifyTechnician(selectedForVerify.id)}
                className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" /> Onayla
              </button>
              <button
                onClick={() => {
                  const reason = prompt('Reddetme sebebi (isteğe bağlı):');
                  if (reason !== null) {
                    handleRejectTechnician(selectedForVerify.id, reason);
                  }
                }}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" /> Reddet
              </button>
              <button
                onClick={() => {
                  openEditModal(selectedForVerify);
                  setVerifyModalOpen(false);
                }}
                className="py-3 px-4 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <Edit className="w-5 h-5" /> Düzenle
              </button>
            </div>
          </div>
        )}
      </NativeModal>

      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editingTechnician ? 'Teknisyen Düzenle' : 'Yeni Teknisyen'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Temel Bilgiler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ad Soyad *</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                required
                title="Ad Soyad zorunludur"
                onInvalid={(e) => e.target.setCustomValidity('Ad Soyad alanını doldurun')}
                onInput={(e) => e.target.setCustomValidity('')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefon *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="0532 123 45 67"
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                required
                title="Telefon zorunludur"
                onInvalid={(e) => e.target.setCustomValidity('Telefon numarası girin')}
                onInput={(e) => e.target.setCustomValidity('')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teknisyen Tipi</label>
              <select
                value={form.technician_type}
                onChange={(e) => setForm({ ...form, technician_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
              >
                {Object.entries(TECHNICIAN_TYPES).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
            {form.technician_type !== 'hims_team' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Firma Adi</label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
              </div>
            )}
          </div>

          {/* Uzmanlık Alanları */}
          <div>
            <label className="block text-sm font-medium mb-2">Uzmanlik Alanlari</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SPECIALTIES).map(([key, val]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleSpecialty(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    form.specialties.includes(key)
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>

          {/* Adres ve Konum Bilgileri */}
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <h4 className="font-medium text-orange-800 dark:text-orange-400 mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adres ve Konum (Usta Eşleştirme İçin)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">İl *</label>
                <select
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value, district: '' })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                >
                  <option value="">Seçin...</option>
                  {turkeyProvinces.map(province => (
                    <option key={province.code} value={province.name}>{province.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">İlçe *</label>
                <select
                  value={form.district}
                  onChange={(e) => setForm({ ...form, district: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  disabled={!form.city}
                >
                  <option value="">{form.city ? 'Seçin...' : 'Önce il seçin'}</option>
                  {getDistrictsByCity(form.city).map(ilce => (
                    <option key={ilce} value={ilce}>{ilce}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-1">Hizmet Bölgeleri</label>
                <input
                  type="text"
                  value={form.service_regions_text !== undefined ? form.service_regions_text : (Array.isArray(form.service_regions) ? form.service_regions.join(', ') : (form.service_regions || ''))}
                  onChange={(e) => setForm({ ...form, service_regions_text: e.target.value, service_regions: e.target.value })}
                  placeholder="İstanbul, Kocaeli, Bursa"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
                <p className="text-xs text-slate-500 mt-1">Virgülle ayırın</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Açık Adres</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Sokak, mahalle, bina no..."
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
              />
            </div>
          </div>

          {/* Ödeme Bilgileri */}
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h4 className="font-medium text-green-800 dark:text-green-400 mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Odeme Bilgileri
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Odeme Tipi</label>
                <select
                  value={form.payment_type}
                  onChange={(e) => setForm({ ...form, payment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                >
                  {Object.entries(PAYMENT_TYPES).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">İş Başı Ücreti (TL)</label>
                <input
                  type="number"
                  value={form.per_job_rate}
                  onChange={(e) => setForm({ ...form, per_job_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Garanti Ucreti (TL)</label>
                <input
                  type="number"
                  value={form.warranty_rate}
                  onChange={(e) => setForm({ ...form, warranty_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Yol Masrafi (TL)</label>
                <input
                  type="number"
                  value={form.travel_allowance}
                  onChange={(e) => setForm({ ...form, travel_allowance: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
              </div>
            </div>
            {form.payment_type === 'commission' && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Komisyon Orani (%)</label>
                <input
                  type="number"
                  value={form.commission_rate}
                  onChange={(e) => setForm({ ...form, commission_rate: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  className="w-full md:w-1/4 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                />
              </div>
            )}
          </div>

          {/* Banka Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">IBAN</label>
              <input
                type="text"
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="TR00 0000 0000 0000 0000 0000 00"
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vergi No / TC</label>
              <input
                type="text"
                value={form.tax_id}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
              />
            </div>
          </div>

          {/* Üyelik Bilgileri */}
          {editingTechnician && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <h4 className="font-medium text-purple-800 dark:text-purple-400 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Üyelik Bilgileri
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Üyelik Başlangıç Tarihi</label>
                  <input
                    type="date"
                    value={form.membership_start}
                    onChange={(e) => setForm({ ...form, membership_start: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Üyelik Bitiş Tarihi</label>
                  <input
                    type="date"
                    value={form.membership_end}
                    onChange={(e) => setForm({ ...form, membership_end: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  />
                </div>
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                Bu tarihler teknisyen panelindeki "Üyelik" sekmesinde görünür.
              </p>
            </div>
          )}

          {/* Kullanıcı Hesabı - Portal Girişi */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-medium text-blue-800 dark:text-blue-400 mb-3 flex items-center gap-2">
              <User className="w-5 h-5" />
              Portal Giriş Hesabı
            </h4>
            
            {form.has_user_account ? (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span>Bu teknisyenin zaten bir kullanıcı hesabı var</span>
              </div>
            ) : (
              <>
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={form.create_user_account}
                    onChange={(e) => setForm({ ...form, create_user_account: e.target.checked })}
                    className="w-5 h-5 rounded border-blue-300 text-blue-600"
                  />
                  <div>
                    <span className="font-medium">Kullanıcı Hesabı Oluştur</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Teknisyen Usta Portal'a giriş yapabilir
                    </p>
                  </div>
                </label>

                {form.create_user_account && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-blue-200 dark:border-blue-700">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email (Giriş için)*</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="usta@firma.com"
                        className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Şifre*</label>
                      <input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="En az 6 karakter"
                        className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-slate-900"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Durum */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm">Aktif</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_available}
                onChange={(e) => setForm({ ...form, is_available: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm">Müsait</span>
            </label>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t dark:border-slate-700">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
            >
              Iptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                editingTechnician ? 'Güncelle' : 'Oluştur'
              )}
            </button>
          </div>
        </form>
      </NativeModal>

      {/* Sözleşme Oluşturma Modal */}
      <NativeModal
        isOpen={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        title="Yeni Sözleşme Oluştur"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleCreateContract(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Usta *</label>
            <select
              value={contractForm.technician_id}
              onChange={(e) => setContractForm({ ...contractForm, technician_id: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
              required
            >
              <option value="">Usta seçin...</option>
              {technicians.filter(t => t.is_verified !== false).map(tech => (
                <option key={tech.id} value={tech.id}>{tech.full_name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Site *</label>
            <select
              value={contractForm.site_id}
              onChange={(e) => setContractForm({ ...contractForm, site_id: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
              required
            >
              <option value="">Site seçin...</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Başlangıç Tarihi *</label>
              <input
                type="date"
                value={contractForm.start_date}
                onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bitiş Tarihi *</label>
              <input
                type="date"
                value={contractForm.end_date}
                onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Aylık Ücret (₺)</label>
            <input
              type="number"
              value={contractForm.monthly_fee}
              onChange={(e) => setContractForm({ ...contractForm, monthly_fee: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Notlar</label>
            <textarea
              value={contractForm.notes}
              onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
              placeholder="Sözleşme hakkında notlar..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setContractModalOpen(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Oluştur
            </button>
          </div>
        </form>
      </NativeModal>
    </div>
  );
}
